import { useState, useMemo, lazy, Suspense, useCallback } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { SuppressPolicyGeneratedEmailCheckbox } from '@components/Subjectivities/SuppressPolicyGeneratedEmailCheckbox';
import { Table } from '@components/table/Table';
import type { TableFilterConfig } from '@components/table/Table';
import { TableFilter } from '@components/table/types';
import {
  ConfirmationDialog,
  AddFilesDialog,
  ViewFilesDialog,
  MarkSubjectivityCompleteDialog,
} from '@components/dialog';
import { acceptedFileMimeTypes } from '@utils/fileUtils';
import cuid from 'cuid';
import { documentQueryKey, useCreateDocument, useDeleteDocument } from '@services/document';
import { uploadFile, useCreateFile, useDeleteFile } from '@services/file';
import type { PostArtifact } from '@models/note';
import { DocumentType } from '@models/document';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateSubjectivity, useSearchSubjectivities } from '@services/subjectivity';
import type { SubjectivityItem } from '@services/subjectivity';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { SubjectivityStatus } from '@lio/rule-engine';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { QuoteType, QuoteTypeLabels } from '@models/quotes';
import { getQuoteSubjectivityColumns, getPolicySubjectivityColumns } from './getSubjectivityColumns';

const SubjectivityFormModal = lazy(() =>
  import('@pages/QuoteDetails/sidebarItems/subjectivities/SubjectivityFormModal/SubjectivityFormModal').then((m) => ({
    default: m.SubjectivityFormModal,
  })),
);

export interface SubjectivityTableRow extends SubjectivityItem {
  isDisabled?: boolean;
  handleUploadDocument: (item: SubjectivityItem) => void;
  handleMarkAsComplete: (item: SubjectivityItem) => void;
  handleDelete?: () => void;
  handleOpenViewFilesModal: () => void;
  handleOpenFormDialog: () => void;
}

type SubjectivityTableProps = {
  variant: 'quote' | 'policy';
  transactionNumber: string;
  quoteId?: string;
  policyId?: string;
  rowFormatter?: (rows: SubjectivityTableRow[]) => SubjectivityTableRow[];
  sxProps?: {
    tableContainer?: SxProps<Theme>;
    dataGridContainer?: SxProps<Theme>;
    dataGrid?: SxProps<Theme>;
  };
  hidePagination?: boolean;
};

export const SubjectivityTable = ({
  variant,
  transactionNumber,
  quoteId,
  policyId,
  rowFormatter: externalRowFormatter,
  sxProps,
  hidePagination,
}: SubjectivityTableProps) => {
  const { isInternalUser, isLioSales } = useStore((state) => state.userAuth, shallow);
  const client = useQueryClient();

  const { createDocumentAsync } = useCreateDocument();
  const { deleteFileAsync } = useDeleteFile();
  const { deleteDocumentAsync } = useDeleteDocument();
  const { createFileAsync } = useCreateFile();
  const { updateSubjectivity } = useUpdateSubjectivity(quoteId);

  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [viewFilesDialog, setViewFilesDialog] = useState(false);
  const [disableLastFileDelete, setDisableLastFileDelete] = useState(false);
  const [selectedSubjectivity, setSelectedSubjectivity] = useState<SubjectivityItem | undefined>();
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [formReadOnly, setFormReadOnly] = useState(false);
  const [markCompleteDialogOpen, setMarkCompleteDialogOpen] = useState(false);
  // Tracks whether the action in progress closes the last open subjectivity, which is what triggers the
  // policy package generated email. Only then do we offer the option to suppress that email.
  const [isClosingLastSubjectivity, setIsClosingLastSubjectivity] = useState(false);
  const [suppressPolicyGeneratedEmail, setSuppressPolicyGeneratedEmail] = useState(false);

  // The email is only sent for policies, so the suppression option is scoped to internal users on the policy view.
  const canSuppressPolicyGeneratedEmail = variant === 'policy' && isInternalUser && isClosingLastSubjectivity;

  const handleDeleteDocument = async () => {
    await deleteDocumentAsync({ documentId: selectedDocumentId });
    if (selectedSubjectivity && selectedSubjectivity.documents.length <= 1) {
      if (viewFilesDialog) {
        setViewFilesDialog(false);
      }
      await updateSubjectivity({ status: SubjectivityStatus.Active, subjectivityId: selectedSubjectivity.id });
      setSelectedSubjectivity(undefined);
    } else {
      setSelectedSubjectivity((prev) => {
        const filteredDocuments = prev?.documents.filter((el) => el.id !== selectedDocumentId) ?? [];
        return { ...prev, documents: filteredDocuments } as SubjectivityItem;
      });
    }
    setConfirmDialogOpen(false);
  };

  const onAddNewFile = async (files: PostArtifact[]) => {
    await Promise.all(
      files
        .filter((f) => acceptedFileMimeTypes.includes(f.file.type))
        .map(async (f) => {
          let createdFileId;
          let createdDocumentId;
          try {
            const documentId = cuid();
            const file = await createFileAsync({
              description: f.description,
              awsS3Id: `document/${documentId}`,
              fileName: f.name,
              originalFileName: f.name,
              fileType: f.mimetype,
            });

            if (!file.uploadUrl) {
              throw new Error('Invalid file generated');
            }

            createdFileId = file.id;
            await uploadFile(file.uploadUrl, f.file);
            const document = await createDocumentAsync({
              fileId: file.id,
              subjectivityId: selectedSubjectivity?.id,
              documentTypeId: DocumentType.Other.id,
            });
            createdDocumentId = document.id;
            return document.id;
          } catch (e) {
            if (createdFileId) {
              await deleteFileAsync({ fileId: createdFileId });
            }
            if (createdDocumentId) {
              await deleteDocumentAsync({ documentId: createdDocumentId });
            }
            return undefined;
          }
        }),
    );
    if (selectedSubjectivity) {
      await updateSubjectivity({
        status: SubjectivityStatus.Closed,
        subjectivityId: selectedSubjectivity.id,
        ...(canSuppressPolicyGeneratedEmail
          ? { suppressSubjectivityCompletePolicyGeneratedEmail: suppressPolicyGeneratedEmail }
          : {}),
      });
    }
    client.refetchQueries({ queryKey: documentQueryKey.search });
    setSelectedSubjectivity(undefined);
  };

  const baseRowFormatter = useCallback(
    (rows: SubjectivityItem[]): SubjectivityTableRow[] => {
      const visibleRows = variant === 'quote' ? rows.filter((it) => !it.deletedAt) : rows;
      const openSubjectivities = visibleRows.filter(
        (it) => !it.isBrokerSubjectivity && it.status === SubjectivityStatus.Active,
      );
      const allClosed = visibleRows.every((it) => it.status === SubjectivityStatus.Closed);

      return visibleRows.map((it) => ({
        ...it,
        handleUploadDocument: (item: SubjectivityItem) => {
          setSelectedSubjectivity(item);
          setConfirmDialogOpen(false);
          setIsClosingLastSubjectivity(openSubjectivities.length === 1 && openSubjectivities[0].id === item.id);
          setSuppressPolicyGeneratedEmail(false);
          setFilesDialogOpen(true);
        },
        handleMarkAsComplete: (item: SubjectivityItem) => {
          setSelectedSubjectivity(item);
          setIsClosingLastSubjectivity(openSubjectivities.length === 1 && openSubjectivities[0].id === item.id);
          setMarkCompleteDialogOpen(true);
        },
        handleDelete:
          variant === 'quote' || (isInternalUser && openSubjectivities.length > 0)
            ? () => {
                setConfirmDialogOpen(true);
                setSelectedDocumentId(it.documents[0].id);
                setSelectedSubjectivity(it);
              }
            : undefined,
        handleOpenViewFilesModal: () => {
          setSelectedSubjectivity(it);
          setViewFilesDialog(true);
          if (variant === 'policy') {
            setDisableLastFileDelete(allClosed);
          }
        },
        handleOpenFormDialog: () => {
          setSelectedSubjectivity(it);
          if (variant === 'policy') {
            setFormReadOnly(openSubjectivities.length === 0);
          }
          setIsClosingLastSubjectivity(openSubjectivities.length === 1 && openSubjectivities[0].id === it.id);
          setIsFormDialogOpen(true);
        },
      }));
    },
    [variant, isInternalUser],
  );

  const combinedRowFormatter = useCallback(
    (rows: SubjectivityItem[]) => {
      const augmented = baseRowFormatter(rows);
      return externalRowFormatter ? externalRowFormatter(augmented) : augmented;
    },
    [baseRowFormatter, externalRowFormatter],
  );

  const columns = useMemo(
    () =>
      variant === 'quote'
        ? getQuoteSubjectivityColumns({ isInternalUser, isLioSales })
        : getPolicySubjectivityColumns({ isInternalUser, isLioSales }),
    [variant, isInternalUser, isLioSales],
  );

  const filterConfig = useMemo<TableFilterConfig>(() => {
    if (variant === 'policy') {
      return {
        filters: [
          {
            field: 'quoteType',
            label: 'Transaction Type',
            width: { setWidth: 350 },
            type: TableFilter.SELECT_SINGLE,
            other: {
              options: [QuoteType.NewBusiness, QuoteType.Rewrite, QuoteType.Renewal].map((qt) => ({
                label: QuoteTypeLabels[qt],
                value: qt,
              })),
            },
          },
        ],
        defaultFilters: { policyId },
        filterQueryParam: { policyId: 'policyId', quoteType: 'quoteType' } as Record<string, string>,
      };
    }
    return {
      filterQueryParam: { quoteId: 'quoteId' } as Record<string, string>,
      defaultFilters: { quoteId },
    };
  }, [variant, quoteId, policyId]);

  const defaultSxProps = {
    tableContainer: { ...styles.dataGridContainer, display: 'block', height: 'fit-content' },
    dataGridContainer: { ...styles.dataGridContainer, borderLeft: 'none', padding: 0, height: 'auto' },
    dataGrid: styles.dataGrid,
  };

  const titlePrefix = variant === 'quote' ? 'Quote' : 'Policy';

  return (
    <>
      <Table
        ariaLabel='subjectivities-grid'
        columns={columns}
        onLoadData={useSearchSubjectivities as any}
        rowFormatter={combinedRowFormatter}
        hidePagination={hidePagination}
        paginationConfig={variant === 'quote' ? { currentPage: 0, rowsPerPage: 100, pageOffset: 0 } : undefined}
        noResultsConfig={{ noResultsMessage: 'No Subjectivities found' }}
        filterConfig={filterConfig}
        sxProps={sxProps ?? defaultSxProps}
      />
      <ConfirmationDialog
        isOpen={confirmDialogOpen}
        description='Are you sure you want to delete the existing file?'
        onCancel={() => setConfirmDialogOpen(false)}
        buttonConfig={{ confirmText: 'Yes', secondaryText: 'No', onConfirm: handleDeleteDocument }}
      />
      <AddFilesDialog
        open={filesDialogOpen}
        handleCloseDialog={() => setFilesDialogOpen(false)}
        onDone={({ uploadedFiles }) => onAddNewFile(uploadedFiles)}
        additionalContent={
          canSuppressPolicyGeneratedEmail ? (
            <SuppressPolicyGeneratedEmailCheckbox
              checked={suppressPolicyGeneratedEmail}
              onChange={setSuppressPolicyGeneratedEmail}
            />
          ) : undefined
        }
      />
      <ViewFilesDialog
        open={viewFilesDialog}
        disableDeleteLastFile={disableLastFileDelete}
        label={selectedSubjectivity?.label}
        documents={selectedSubjectivity?.documents}
        handleClose={() => setViewFilesDialog(false)}
        handleDeleteFile={(documentId?: string) => {
          setConfirmDialogOpen(true);
          if (documentId) {
            setSelectedDocumentId(documentId);
          }
        }}
      />
      <Suspense fallback={null}>
        {isFormDialogOpen && selectedSubjectivity?.subjectivityType && (
          <SubjectivityFormModal
            isOpen={true}
            subjectivity={selectedSubjectivity}
            title={`${titlePrefix} #${transactionNumber} ${selectedSubjectivity.label}`}
            quoteId={quoteId}
            onClose={() => {
              setSelectedSubjectivity(undefined);
              setIsFormDialogOpen(false);
            }}
            isReadOnly={formReadOnly}
            showSuppressEmailOption={canSuppressPolicyGeneratedEmail}
          />
        )}
      </Suspense>
      {markCompleteDialogOpen && selectedSubjectivity && transactionNumber && (
        <MarkSubjectivityCompleteDialog
          isOpen={markCompleteDialogOpen}
          subjectivity={selectedSubjectivity}
          transactionNumber={transactionNumber}
          showSuppressEmailOption={canSuppressPolicyGeneratedEmail}
          onClose={() => {
            setMarkCompleteDialogOpen(false);
            setSelectedSubjectivity(undefined);
          }}
        />
      )}
    </>
  );
};
